import { toast } from "react-toastify";
import type { CustomFlowbiteTheme } from "flowbite-react/types";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  Spinner,
  Textarea,
  TextInput,
} from "flowbite-react";
import { useEffect, useState } from "react";
import {
  getAllEquipment,
  getAllExerciseTypes,
  getAllMuscles,
  getAllMuscleGroups,
  getExerciseDetails,
  searchEquipment,
} from "../../services/exercises";
import type { Equipment, ExerciseForm, Muscle } from "../../types/apiResponses";
import { useQuery } from "@tanstack/react-query";
import FileUploadZone, { ImageFile } from "../media/FileUploadZone";
import AsyncSelect from "react-select/async";

export const blueThemeSpinner: CustomFlowbiteTheme["spinner"] = {
  color: {
    info: "fill-blue-700",
  },
};

export const ThemeTooltip: CustomFlowbiteTheme["tooltip"] = {
  target: "w-full",
};

export interface ExerciseModalProps {
  id?: string;
  updating: boolean;
  exercise: ExerciseForm;
  isOpen: boolean;
  handleSubmit: (
    exerciseData: ExerciseForm | Partial<ExerciseForm>,
  ) => Promise<void>;
  handleClose: () => void;
}

const ExerciseModal = function (props: ExerciseModalProps) {
  const {
    id,
    isOpen,
    updating,
    exercise: initialExerciseProp,
    handleSubmit,
    handleClose,
  } = props;

  const [internalExercise, setInternalExercise] =
    useState<ExerciseForm>(initialExerciseProp);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    setInternalExercise(initialExerciseProp);
    setTouchedFields({});
  }, [initialExerciseProp]);

  const typesData = useQuery({
    queryKey: ["typesData"],
    queryFn: () => fetchTypesData(),
  });

  const exerciseDetails = useQuery({
    queryKey: ["exerciseDetails", id],
    queryFn: () => fetchExerciseDetails(),
    enabled: !!id,
  });

  const equipment = useQuery({
    queryKey: ["equipment"],
    queryFn: async () => {
      const response = await getAllEquipment();
      return response;
    },
  });

  const muscleGroups = useQuery({
    queryKey: ["muscleGroups"],
    queryFn: async () => {
      const response = await getAllMuscleGroups();
      return response;
    },
  });

  const isLoading =
    typesData.isLoading ||
    exerciseDetails.isLoading ||
    equipment.isLoading ||
    muscleGroups.isLoading;

  const fetchExerciseDetails = async () => {
    if (id) {
      const exerciseData = await getExerciseDetails(id);
      return {
        id: exerciseData.results.id,
        name: exerciseData.results.name,
        description: exerciseData.results.description || "",
        repCount: exerciseData.results.repCount || "",
        setCount: exerciseData.results.setCount || "",
        exerciseType: exerciseData.results.exerciseType.id,
        equipment:
          exerciseData.results.equipment?.map((eq: Equipment) => eq.id) || [],
        muscles: exerciseData.results.muscles.map((x: any) => x.id),
        muscleGroups:
          exerciseData.results.muscleGroups?.map((mg: any) => mg.id) || [],
        imageUrl: exerciseData.results.imageUrl || "",
      };
    }
    return null;
  };

  const fetchTypesData = async () => {
    const musclesData = (await getAllMuscles()).results;
    const typesData = (await getAllExerciseTypes()).results;
    const muscleGroupsData = (await getAllMuscleGroups()).results;
    return {
      typesData,
      musclesData,
      muscleGroupsData,
    };
  };

  useEffect(() => {
    if (id && exerciseDetails.data) {
      setInternalExercise(exerciseDetails.data as ExerciseForm);
      setTouchedFields({});
    }
  }, [id, exerciseDetails.data]);

  const handleInput = (e: any) => {
    const { name, value } = e.target;
    setInternalExercise((prev) => ({ ...prev, [name]: value }));
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
  };

  const handleSelectMuscle = (e: any) => {
    const muscleId = e.target.value;
    setInternalExercise((prev) => {
      const newMuscles = prev.muscles.includes(muscleId)
        ? prev.muscles.filter((x) => x !== muscleId)
        : [...prev.muscles, muscleId];
      return { ...prev, muscles: newMuscles };
    });
    setTouchedFields((prev) => ({ ...prev, muscles: true }));
  };

  const handleSelectType = (e: any) => {
    const newExerciseType = e.target.value;
    const currentType = typesData.data?.typesData?.find(
      (type: any) => type.id === internalExercise.exerciseType,
    );
    const newType = typesData.data?.typesData?.find(
      (type: any) => type.id === newExerciseType,
    );

    const wasHiit = currentType?.name?.toLowerCase().includes("hiit");
    const isHiit = newType?.name?.toLowerCase().includes("hiit");

    setInternalExercise((prev) => ({
      ...prev,
      exerciseType: newExerciseType,
      repCount: wasHiit !== isHiit ? "" : prev.repCount,
    }));
    setTouchedFields((prev) => ({
      ...prev,
      exerciseType: true,
      ...(wasHiit !== isHiit && { repCount: true }),
    }));
  };

  const loadEquipmentOptions = async (inputValue: string) => {
    try {
      const result = await searchEquipment(inputValue);
      return result.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    } catch (error) {
      console.error("Error loading equipment options:", error);
      return [];
    }
  };

  const [selectedEquipment, setSelectedEquipment] = useState<any[]>([]);

  useEffect(() => {
    if (!equipment.data) {
      return;
    }

    if (internalExercise.equipment && internalExercise.equipment.length > 0) {
      try {
        const allAvailableEquipment: Equipment[] = equipment.data;
        const selectedEquipmentIDs: string[] = internalExercise.equipment;
        const selectedEquipmentIDSet = new Set(selectedEquipmentIDs);

        const itemsToDisplayInSelect = allAvailableEquipment
          .filter((eqDetail) => selectedEquipmentIDSet.has(eqDetail.id))
          .map((eqDetail) => ({
            value: eqDetail.id,
            label: eqDetail.name,
          }));
        setSelectedEquipment(itemsToDisplayInSelect);
      } catch (error) {
        console.error(
          "Error mapping selected equipment IDs to display objects:",
          error,
        );
        setSelectedEquipment([]);
      }
    } else {
      setSelectedEquipment([]);
    }
  }, [internalExercise.equipment, equipment.data]);

  const handleEquipmentChange = (selectedOptions: any) => {
    const equipmentIds = selectedOptions
      ? selectedOptions.map((option: any) => option.value)
      : [];

    setInternalExercise((prev) => ({
      ...prev,
      equipment: equipmentIds,
    }));

    setTouchedFields((prev) => ({ ...prev, equipment: true }));
  };

  const handleImageChange = (imageData: ImageFile) => {
    setInternalExercise((prev) => ({
      ...prev,
      image: imageData.dataUri,
    }));
    setTouchedFields((prev) => ({ ...prev, image: true }));
  };

  const getTouchedFields = (): Partial<ExerciseForm> => {
    if (!id) {
      return internalExercise;
    }

    const touchedData: Partial<ExerciseForm> = {};
    for (const key in touchedFields) {
      if (touchedFields[key] === true) {
        const formKey = key as keyof ExerciseForm;
        if (formKey in internalExercise) {
          (touchedData as any)[formKey] = internalExercise[formKey];
        }
      }
    }
    return touchedData;
  };

  const validateForm = () => {
    if (!internalExercise.exerciseType) {
      toast.error("Please select an Exercise Type");
      return false;
    }
    return true;
  };

  const handleFormSubmit = async () => {
    if (validateForm()) {
      if (id) {
        const dataToSubmit = getTouchedFields();
        console.log("Updating with fields:", Object.keys(dataToSubmit));
        await handleSubmit(dataToSubmit);
      } else {
        console.log("Creating with exercise data:", internalExercise);
        await handleSubmit(internalExercise);
      }
      handleClose();
    }
  };

  return (
    <Modal size="3xl" position="top-center" show={isOpen} onClose={handleClose}>
      <ModalHeader className="border-b border-gray-200 p-6! dark:border-gray-700">
        <strong>{id ? "Edit" : "Add"} Exercise</strong>
      </ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="flex min-h-52 items-center justify-center ">
            <Spinner theme={blueThemeSpinner} color="info" size="xl" />
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <Label htmlFor="name" className="font-semibold">
                  Name
                </Label>
                <div className="mt-1">
                  <TextInput
                    id={`name-${internalExercise.id || "new"}`}
                    name="name"
                    value={internalExercise?.name || ""}
                    placeholder="Name"
                    onChange={handleInput}
                  />
                </div>
              </div>
              <div>
                <Label className="font-semibold">Exercise Type</Label>
                <div className="mt-1">
                  <Select onChange={handleSelectType} name="exercise">
                    <option selected disabled>
                      Choose
                    </option>
                    {typesData.data?.typesData.map((x: any) => {
                      return (
                        <option
                          key={x.id}
                          value={x.id}
                          selected={x.id === internalExercise.exerciseType}
                        >
                          {x.name}
                        </option>
                      );
                    })}
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-semibold">Equipment</Label>
                <div className="mt-1">
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    defaultOptions
                    loadOptions={loadEquipmentOptions}
                    onChange={handleEquipmentChange}
                    value={selectedEquipment}
                    placeholder="Search and select equipment..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              </div>
              <div>
                {typesData.data?.typesData
                  ?.find(
                    (type: any) => type.id === internalExercise.exerciseType,
                  )
                  ?.name?.toLowerCase()
                  .includes("hiit") ? (
                  <>
                    <Label className="font-semibold">Duration</Label>
                    <div className="mt-1">
                      <Select
                        name="repCount"
                        value={internalExercise?.repCount || ""}
                        onChange={handleInput}
                      >
                        <option value="">Choose duration</option>
                        <option value="30 seconds">30 seconds</option>
                        <option value="60 seconds">60 seconds</option>
                      </Select>
                    </div>
                  </>
                ) : (
                  <>
                    <Label htmlFor="repCount" className="font-semibold">
                      Rep Count
                    </Label>
                    <div className="mt-1">
                      <TextInput
                        id={`repCount-${internalExercise.id || "new"}`}
                        name="repCount"
                        value={internalExercise?.repCount || ""}
                        placeholder="e.g. 10-12"
                        onChange={handleInput}
                      />
                    </div>
                  </>
                )}
              </div>
              <div>
                <Label htmlFor="setCount" className="font-semibold">
                  Set Count
                </Label>
                <div className="mt-1">
                  <TextInput
                    id={`setCount-${internalExercise.id || "new"}`}
                    name="setCount"
                    value={internalExercise?.setCount || ""}
                    placeholder="e.g. 3-4"
                    onChange={handleInput}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="image" className="font-semibold">
                  Exercise Image
                </Label>
                <FileUploadZone
                  acceptedTypes={[
                    "image/jpeg",
                    "image/png",
                    "image/webp",
                    "image/gif",
                  ]}
                  maxSize={5242880}
                  defaultPreview={
                    internalExercise.imageUrl
                      ? {
                          url: internalExercise.imageUrl,
                          type: "image/webp",
                          name: "Current Image",
                        }
                      : undefined
                  }
                  onImageChange={handleImageChange}
                  maxDimensions={{ width: 2000, height: 2000 }}
                />
                {internalExercise.imageUrl && (
                  <p className="mt-2 text-xs text-gray-500">
                    Current image: {internalExercise.imageUrl.split("/").pop()}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="description" className="font-semibold">
                  Description
                </Label>
                <div className="mt-1">
                  <Textarea
                    id={`description-${internalExercise.id || "new"}`}
                    name="description"
                    value={internalExercise?.description || ""}
                    placeholder="Exercise description"
                    onChange={handleInput}
                    rows={4}
                  />
                </div>
              </div>
            </div>
            <div className="py-4">
              <Label className="font-semibold">Muscle Target</Label>
              {typesData.data?.musclesData &&
              typesData.data?.musclesData.length > 0 ? (
                <div className="mt-4 grid grid-cols-4 gap-6">
                  {typesData.data?.musclesData.map(
                    (muscle: Muscle, index: number) => (
                      <div key={index} className="flex items-center gap-2">
                        <Button
                          onClick={() =>
                            handleSelectMuscle({ target: { value: muscle.id } })
                          }
                          className={`w-full ${internalExercise?.muscles?.includes(muscle.id) ? "bg-primary-700" : "bg-primary-300"}`}
                        >
                          {muscle.name}
                        </Button>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div className="flex min-h-52 w-full items-center justify-center ">
                  <Spinner theme={blueThemeSpinner} color="info" size="xl" />
                </div>
              )}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {id ? (
          <Button
            color="primary"
            disabled={updating}
            onClick={handleFormSubmit}
          >
            {updating ? "Updating..." : "Update"}
          </Button>
        ) : (
          <Button
            color="primary"
            disabled={updating}
            onClick={handleFormSubmit}
          >
            {updating ? "Creating..." : "Create"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ExerciseModal;
